import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UtilsService } from './utils.service';
import { AuthService } from './auth.service';
import { OidcLogin } from '../models/oidc/OidcLogin';
import { Token } from '../models/Token';

/**
 * Entra ID (OIDC) federated login. Mirrors EraService's shape - a login
 * step that redirects to an external hosted login page, then a confirm
 * step handled once the browser comes back - since UnathorizedInterceptor
 * already special-cases that pattern for EraService (see
 * isSsoConfirmInProgress below).
 */
@Injectable({
  providedIn: 'root'
})
export class SsoService {
  private apiBaseUrl = environment.apiBaseUrl;

  // Same rationale as EraService.eraConfirmInProgress: while the exchange
  // code from GET /oidc/callback is being traded for a session token via
  // POST /oidc/token, there is briefly no valid session yet. If that
  // request itself 400s (invalid/expired exchange code),
  // UnathorizedInterceptor must not treat it as a logout of an existing
  // session - this component's own catch block shows the error instead.
  private ssoConfirmInProgress = false;

  isSsoConfirmInProgress(): boolean {
    return this.ssoConfirmInProgress;
  }

  setSsoConfirmInProgress(value: boolean): void {
    this.ssoConfirmInProgress = value;
  }

  constructor(
    private http: HttpClient,
    private utilsService: UtilsService,
    private authService: AuthService
  ) {}

  /**
   * Starts an Entra ID login for the given organization's login URL
   * segment. Returns the { login_url } to redirect the browser to, or null
   * if this organization has SSO disabled.
   */
  async ssoConfig(loginSlug: string): Promise<{ enabled: boolean }> {
    const url = `${this.apiBaseUrl}/oidc/config/${encodeURIComponent(
      loginSlug
    )}`;
    return lastValueFrom(
      this.http
        .get<{ enabled: boolean }>(url)
        .pipe(catchError(this.utilsService.handleErr))
    );
  }

  ssoLogin(loginSlug: string): Observable<OidcLogin> {
    const url = `${this.apiBaseUrl}/oidc/login/${encodeURIComponent(
      loginSlug
    )}`;
    return this.http
      .get<OidcLogin>(url)
      .pipe(catchError(this.utilsService.handleErr));
  }

  /**
   * Exchanges the one-time code from the /oidc/callback redirect for an
   * Aline session, and applies it exactly like a password login would
   * (AuthService.applySessionToken).
   */
  async ssoConfirm(exchangeCode: string): Promise<void> {
    const url = `${this.apiBaseUrl}/oidc/token`;
    const token = await lastValueFrom(
      this.http
        .post<Token>(url, { xc: exchangeCode })
        .pipe(catchError(this.utilsService.handleErr))
    );
    this.authService.applySessionToken(token);
  }
}
